"use client";
import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  useDisclosure,
} from "@nextui-org/react";
import { useDictionary } from "../../context/dictionaryProvider";
import { ContactWrapShape, FillerBtn, SendBtn, TextButton } from "../molecules";
import InputTextField from "../atoms/InputTextField";
import PhoneField from "../atoms/PhoneField";
import { useForm } from "react-hook-form";
import { downloadBrochureAction } from "../../actions/brochure";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { bitrixSourceIds, getImageUrl } from "../../lib/util";
import { trackEnquiryTypeFormSubmit } from "../../lib/tracking";
import { bitrixLeadAction } from "../../actions/bitrix";
import { zohoLeadAction } from "../../actions/zoho";
import Loading from "../../app/[lang]/loading";
import Link from "next/link";
import ThankuModal from "./ThankuModal";
import { useSearchParams } from "next/navigation";
import { isPhoneValid } from "../../lib/validatePhone";
import { leadFields } from "../../emailTemplates/leadTemplate";
import { leadEmailAction } from "../../actions/email";
import EnquiryTypeToggle from "../atoms/EnquiryTypeToggle";
import { isCorporateEmail } from "../../lib/emailDomain";
import { trackMetaEvent } from "../../lib/metaTracking";
import { trackEvent } from "../../lib/engageXClient";
import { useFormTracking } from "../../lib/useFormTracking";

const formSchema = yup
  .object({
    name: yup
      .string()
      .required()
      .min(3, "Name must be at least 3 characters long")
      .max(25, "Name cannot exceed 25 characters")
      .matches(/^[a-zA-Z0-9\s]+$/, "No special character allowed"),
    email: yup
      .string()
      .email()
      .required()
      .matches(
        /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/,
        "Email must have a valid domain"
      )
      .test(
        "corporate-email",
        "Please enter a valid work email",
        function (value) {
          if (!this.parent?.isCorporate || !value) return true;
          return isCorporateEmail(value);
        }
      ),
    phone: yup
      .string()
      .required("Phone number is required")
      .test("is-valid-phone", "Phone number is not valid", (value) => {
        if (!value) return false;
        return isPhoneValid(value);
      }),
    message: yup
      .string()
      .trim()
      .required()
      .matches(/^[a-zA-Z0-9\s]+$/, "No special character allowed"),
    isCorporate: yup.boolean().required(),
  })
  .required();

const DownloadBrochure = ({
  document,
  course,
  type,
  submitEventName = "DownloadBrochure", // dynamic submit event name
  closeEventName = "CloseBrochureForm", // dynamic close event name
  location = "listing",
}) => {
  const searchParams = useSearchParams();
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const courseName = course?.course_name || course?.name;
  const ft = useFormTracking({ formName: "brochure", open: isOpen, courseName, trigger: location });
  const dict = useDictionary();
  const [response, setResponse] = useState("");
  const [success, setSuccess] = useState(false);
  const [countryName, setCountryName] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: {
      isCorporate: false,
    },
  });

  const isCorporate = watch("isCorporate", false);

  // Handle form submission
  const onSubmit = async (data) => {
    const dataWithCountry = {
      ...data,
      country_name: countryName || ""
    };
    try {
      if (typeof trackEnquiryTypeFormSubmit === "function") {
        trackEnquiryTypeFormSubmit("download_brochure", data?.isCorporate);
      }
      let res = await downloadBrochureAction({
        ...data,
        course: course?.id,
      });

      if (res?.error) {
        return setResponse(res?.error?.message);
      }

      setResponse("Thank you for your response");
      setSuccess(true);
      trackEvent("brochure_downloaded", {
        course: course?.course_name || course?.name,
        location,
        ...ft.submitted({ isCorporate: data?.isCorporate, country: countryName }),
      });
      trackMetaEvent({
        eventName: "Lead",
        email: data?.email,
        phone: data?.phone,
        customData: {
          content_name: course?.course_name || course?.name || course?.slug,
          content_category: "download_brochure",
          content_ids: course?.slug ? [course.slug] : undefined,
        },
      });

      const utm = {
        utm_source: localStorage.getItem("utm_source") || "",
        utm_medium: localStorage.getItem("utm_medium") || "",
        utm_campaign: localStorage.getItem("utm_campaign") || "",
        utm_term: localStorage.getItem("utm_term") || "",
        utm_content: localStorage.getItem("utm_content") || "",
        click_id: localStorage.getItem("click_id") || "",
      };

      const courseData = {
          id: course?.id,
          name: course?.course_name,
          discuntPrice: course?.discounted_price,
          price: course?.price,
          productId: course?.product_id,
      };

      void Promise.allSettled([
        bitrixLeadAction(
          { ...data, course: courseData },
          searchParams,
          bitrixSourceIds.downloadBoucher
        ),
        zohoLeadAction(
          { ...dataWithCountry, ...utm, course: courseData },
          window.location.href,
          bitrixSourceIds.downloadBoucher,
          "Download Brochure (Website)"
        ),
        leadEmailAction("Brochure", leadFields(data)),
      ]).then((results) => {
        results.forEach((result) => {
          if (result.status === "rejected") {
            console.error("Download brochure integration failed:", result.reason);
          }
        });
      });
    } catch (error) {
      console.log(error);
    } finally {
      reset({
        isCorporate: false,
      });
    }
  };

  // Handle modal open
  const handleClick = (e) => {
    e.stopPropagation();
    trackEvent("brochure_click", { course_name: courseName, location });
    onOpen();
  };



  const ButtonComponent = type === "standard" ? TextButton : FillerBtn;

  return (
    <>
      <ButtonComponent
        onClick={handleClick}
        disabled={!document}
        className={`${document ? "" : "opacity-70"} !capitalize`}
      >
        {dict?.courses?.downloadBrochure}
      </ButtonComponent>

      {isOpen && (
        <Modal
          backdrop="blur"
          isOpen={isOpen}
          size="xl"
          classNames={{
            backdrop: "blur-md",
          }}
          scrollBehavior="outside"
          onOpenChange={onOpenChange}
          onClose={() => {
            setResponse("");
            reset();
            setSuccess(false);
          }}
        >
          <ModalContent>
            {(onClose) => (
              <>
                {!isSubmitting ? (
                  <ModalBody>
                    {!success ? (
                      <form
                        ref={ft.ref}
                        onSubmit={handleSubmit(onSubmit)}
                        className="relative bg-[#fff] my-12 mx:0 md:mx-10"
                      >
                        <div className="px-12 pb-28 pt-10 rounded-2xl border border-black">
                          <h3 className="text-2xl text-[#303030] mb-10 font-bold capitalize">
                            {dict?.courses?.downloadBrochure}
                          </h3>
                          <input type="hidden" {...register("isCorporate")} />
                          <EnquiryTypeToggle
                            isCorporate={isCorporate}
                            onChange={(value) =>
                              setValue("isCorporate", value, {
                                shouldDirty: true,
                                shouldValidate: true,
                              })
                            }
                          />
                          <InputTextField
                            label={dict?.form.fieldNames.name}
                            type="text"
                            isInvalid={!!errors?.name}
                            errorMessage={errors?.name?.message}
                            {...register("name")}
                          />
                          <InputTextField
                            label={
                              isCorporate
                                ? dict?.form?.fieldNames?.work_email || "Work Email"
                                : dict?.form.fieldNames.email
                            }
                            type="email"
                            isInvalid={!!errors?.email}
                            errorMessage={errors?.email?.message}
                            {...register("email")}
                          />
                          <PhoneField
                            label={dict?.form.fieldNames.phone}
                            name="phone"
                            value={getValues("phone")}
                            onChange={(value, metadata) => {
                              setValue("phone", value, { shouldDirty: true, shouldValidate: isDirty });
                              //  Capture country name
                              if (metadata?.country?.name) {
                                setCountryName(metadata.country.name);
                              }
                            }}
                            isInvalid={!!errors?.phone}
                            errorMessage={errors?.phone?.message}
                          />
                          <InputTextField
                            label={dict?.form.fieldNames.message}
                            type="text"
                            isInvalid={!!errors?.message}
                            errorMessage={errors?.message?.message}
                            {...register("message")}
                          />
                          <div className="flex items-center">
                            <span className="text-sm">{response}</span>
                            {success && document?.url && (
                              <>
                                {`: `}
                                <Link
                                  prefetch={false}
                                  href={document?.url}
                                  target="_blank"
                                  className="ml-1 text-sm underline underline-offset-2 text-blue-600 rounded hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                >
                                  {dict?.viewDetail}
                                </Link>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Submit button with dynamic tracking */}
                        <SendBtn
                          type="submit"
                          className="!p-4"
                          id="download-brochure"
                        >
                          {dict?.download}
                        </SendBtn>
                        <ContactWrapShape />
                      </form>
                    ) : (
                      <ThankuModal
                        message="Thank you for downloading"
                        btnText="Open"
                        handleClick={() => {
                          onOpenChange();
                          setResponse("");
                          reset();
                          setSuccess(false);
                          if (document?.url) {
                            return window.open(getImageUrl(document), "_blank");
                          }
                        }}
                      />
                    )}
                  </ModalBody>
                ) : (
                  <Loading />
                )}
              </>
            )}
          </ModalContent>
        </Modal>
      )}
    </>
  );
};

export default DownloadBrochure;
